/**
 * Ejecución automática: a qué hora corre solo el proceso.
 *
 * Hasta ahora el horario vivía en el timer del servidor, o sea fuera del
 * alcance de quien opera el módulo: cambiarlo pedía entrar por SSH. Acá se
 * edita desde el portal.
 *
 * La pantalla muestra, además de la configuración, cuándo fue la última
 * corrida y cómo salió. Sin eso "está activada" no dice nada: lo que se
 * quiere saber es si efectivamente corrió.
 */

import { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Plus, Save, Trash2 } from 'lucide-react';

import { mensajeDe } from '../../api';
import { Aviso } from '../../components/Aviso';
import {
  aHoraValida,
  enmascararHora,
  fechaHoraLegible,
  normalizarHora,
} from '../../lib/fechas';
import { guardarProgramacion, leerProgramacion, type ProgramacionDto } from './api';

/** Una hora nueva arranca acá y no vacía: es el horario más habitual. */
const HORA_NUEVA = '20:30';

export function Programacion(): React.JSX.Element {
  const [datos, setDatos] = useState<ProgramacionDto | null>(null);
  const [activa, setActiva] = useState(false);
  const [horarios, setHorarios] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const p = await leerProgramacion();
      setDatos(p);
      setActiva(p.activa);
      setHorarios(p.horarios);
      setError(null);
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /*
   * Una hora a medio escribir —"8" o "20:"— no se manda: el back la rechaza
   * con un 400 y el operador se queda sin saber cuál de las cinco estaba mal.
   * Se avisa acá, señalando el campo.
   */
  const invalidas = horarios.filter((h) => aHoraValida(h) === null);

  const guardar = async (): Promise<void> => {
    if (invalidas.length > 0) {
      setAviso(null);
      setError(
        `Hay ${String(invalidas.length)} horario(s) incompletos. Se escriben como hh:mm, de 00:00 a 23:59.`,
      );
      return;
    }
    setGuardando(true);
    try {
      const p = await guardarProgramacion({ activa, horarios });
      setDatos(p);
      setActiva(p.activa);
      setHorarios(p.horarios);
      setError(null);
      setAviso('Programación guardada.');
    } catch (err) {
      setAviso(null);
      setError(mensajeDe(err));
    } finally {
      setGuardando(false);
    }
  };

  const cambiarHora = (i: number, valor: string): void => {
    setHorarios((hs) => hs.map((h, j) => (j === i ? valor : h)));
  };

  if (cargando) {
    return (
      <section className="programacion">
        <p className="nota">
          <Loader2 className="girando" size={16} aria-hidden="true" /> Cargando la programación…
        </p>
      </section>
    );
  }

  return (
    <section className="programacion">
      {error !== null && <Aviso tono="error">{error}</Aviso>}
      {aviso !== null && <Aviso tono="ok">{aviso}</Aviso>}

      {datos?.soloLectura === true && (
        <Aviso tono="atencion">
          El módulo está en solo lectura: aunque la programación esté activada, la corrida no va a
          registrar nada en GeoVictoria. Hay que quitar ASISTENCIA_SOLO_LECTURA del .env del back.
        </Aviso>
      )}

      {/*
        El horario se guarda en la base, que es compartida, pero quien lo
        dispara es el servidor. Si esta instancia no es esa, la pantalla lo
        dice: si no, alguien programa una corrida y se queda esperándola.
      */}
      {datos?.automaticoApagado === true && (
        <Aviso tono="atencion">
          Esta instancia del portal no ejecuta el proceso por su cuenta (PROCESO_AUTOMATICO=false).
          Los horarios se guardan y los usa el servidor de producción; desde acá se puede procesar a
          mano.
        </Aviso>
      )}

      <form
        className="tarjeta"
        onSubmit={(ev) => {
          ev.preventDefault();
          void guardar();
        }}
      >
        <label className="interruptor">
          <input
            type="checkbox"
            checked={activa}
            onChange={(ev) => setActiva(ev.target.checked)}
          />
          <span>Ejecutar el proceso automáticamente</span>
        </label>

        <p className="nota">
          {`Las horas van en formato de 24 (hh:mm, de 00:00 a 23:59) y son de ${datos?.zona ?? 'la zona operativa'}. Cada corrida procesa el día y los anteriores configurados, y es segura de repetir: no duplica licencias ni salidas.`}
        </p>

        <fieldset className="horarios" disabled={!activa}>
          <legend>Horarios</legend>

          {horarios.length === 0 ? (
            <p className="nota">Todavía no hay ningún horario. Agregá el primero.</p>
          ) : (
            <ul className="lista-horarios">
              {horarios.map((h, i) => (
                // El índice como clave es correcto acá: la lista se edita en
                // el lugar y no se reordena mientras se escribe.
                <li key={i}>
                  <Clock size={15} aria-hidden="true" />
                  {/*
                    Texto con máscara y no `type="time"`: el nativo muestra
                    AM/PM cuando el navegador está en inglés, sin importar el
                    `lang` de la página. Acá las horas son siempre de 24.
                  */}
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    placeholder="hh:mm"
                    maxLength={5}
                    className={aHoraValida(h) === null ? 'invalida' : undefined}
                    value={h}
                    aria-label={`Horario ${String(i + 1)}`}
                    aria-invalid={aHoraValida(h) === null}
                    onChange={(ev) => cambiarHora(i, enmascararHora(ev.target.value))}
                    onBlur={() => cambiarHora(i, normalizarHora(h))}
                  />
                  <button
                    type="button"
                    className="plano"
                    aria-label={`Quitar el horario ${h}`}
                    onClick={() => setHorarios((hs) => hs.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Botón fantasma: agregar un horario no compite con Guardar. */}
          <button type="button" onClick={() => setHorarios((hs) => [...hs, HORA_NUEVA])}>
            <Plus size={15} aria-hidden="true" />
            Agregar horario
          </button>
        </fieldset>

        <button type="submit" disabled={guardando || invalidas.length > 0}>
          {guardando ? (
            <Loader2 className="girando" size={16} aria-hidden="true" />
          ) : (
            <Save size={16} aria-hidden="true" />
          )}
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </form>

      <dl className="panel-programacion">
        <div>
          <dt>Próxima corrida</dt>
          <dd>
            {datos?.proximaCorrida ? (
              fechaHoraLegible(datos.proximaCorrida, datos.zona)
            ) : (
              <span className="nota">No hay ninguna programada</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Última corrida</dt>
          <dd>
            {datos?.ultimaCorridaEn ? (
              fechaHoraLegible(datos.ultimaCorridaEn, datos.zona)
            ) : (
              <span className="nota">Todavía no corrió</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Resultado</dt>
          <dd>{datos?.ultimoResultado ?? <span className="nota">—</span>}</dd>
        </div>
        <div>
          <dt>Última modificación</dt>
          <dd>
            {datos?.actualizadoEn ? (
              `${fechaHoraLegible(datos.actualizadoEn, datos.zona)} — ${datos.actualizadoPor}`
            ) : (
              <span className="nota">—</span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}
